import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Link,
  CircularProgress
} from '@mui/material';
import { CalendarEventDto } from '../../types/calendar';
import { getImageUrl } from '../../utils/image-url.util';
import { workoutService, WorkoutIntakeDetailDto } from '../../services/workout.service';
import { ModalIndices } from '../../common/i-doc/i-doc.registry.ts';

interface WorkoutEventDetailModalProps {
  open: boolean;
  onClose: () => void;
  event: CalendarEventDto | null;
  onEdit?: (event: CalendarEventDto) => void;
}

export const WorkoutEventDetailModal: React.FC<WorkoutEventDetailModalProps> = ({
  open,
  onClose,
  event,
  onEdit,
}) => {
  const [workout, setWorkout] = useState<WorkoutIntakeDetailDto | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !event) {
      setWorkout(null);
      return;
    }
    const workoutId = event.associationId;
    if (workoutId) {
      setLoading(true);
      workoutService.getWorkoutDetail(workoutId)
        .then(full => setWorkout(full))
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      setWorkout(null);
    }
  }, [open, event]);

  if (!event) return null;

  // Extract details safely
  const details = event.details || {};
  const workoutName = event.title || 'Тренування';
  const duration = details.durationMinutes || 0;
  const qty = details.qty; // Get quantity if available
  const calories = (details as any)?.calc?.cal || (event as any).calories || 0;

  const metValue = workout?.metValue ?? details.metValue;
  const description = workout?.details ?? details.details ?? '';
  const externalLink = workout?.externalLink ?? details.externalLink ?? (event as any).externalLink;
  const imageUrl = workout?.imageUrl ?? (event as any).imageUrl ?? details.imageUrl;

  // Met Activity Info
  const metActivityName = workout?.metActivity?.name;

  // Format date
  const dateObj = new Date(event.scheduledLdt!);
  const scheduledTime = dateObj.toLocaleString('uk-UA', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth data-i-doc={ModalIndices.WORKOUT_EVENT_DETAIL}>
      <DialogTitle sx={{ pb: 1 }}>{workoutName}</DialogTitle>
      
      <DialogContent sx={{ pt: 0 }}>
        {loading && !workout && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {/* Зображення */}
        {imageUrl && (
          <Box mb={2}>
            <Box
              component="img"
              src={getImageUrl(imageUrl)}
              alt={workoutName}
              sx={{
                width: '100%',
                maxHeight: 180,
                borderRadius: 2,
                objectFit: 'cover',
                display: 'block'
              }}
            />
          </Box>
        )}

        {/* Час */}
        <Typography variant="body2" color="text.secondary" mb={1.5}>
          {event.isCompleted ? 'Виконано' : 'Заплановано'}: {scheduledTime}
        </Typography>

        {/* Кількість (якщо є, наприклад, повторення) */}
        {qty && (
          <Box mb={1.5}>
            <Typography variant="body2">
              Кількість: <strong>{qty}</strong>
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 1.5 }} />

        {/* Блок з метриками (Ккал, MET, Тривалість) - Стиль NutritionCells */}
        <Box sx={{ display: 'flex', justifyContent: 'space-around', py: 1 }}>
          <Box sx={{ textAlign: 'center', minWidth: 60 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              ккал
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {calories > 0 ? calories : '—'}
            </Typography>
          </Box>
          
          <Box sx={{ textAlign: 'center', minWidth: 60 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              MET
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {metValue || '—'}{metActivityName ? '*' : ''}
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'center', minWidth: 60 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              хв
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {duration > 0 ? duration : '—'}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Опис / Нотатки */}
        {description && (
          <>
            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
              Опис
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.6, whiteSpace: 'pre-line', mb: 1.5 }}>
              {description}
            </Typography>
            <Divider sx={{ my: 1.5 }} />
          </>
        )}

        {/* Посилання */}
        {externalLink && (
          <>
            <Link 
              href={externalLink} 
              target="_blank" 
              rel="noopener noreferrer" 
              variant="caption" 
              sx={{ display: 'block', mt: 1, mb: 1.5, textDecoration: 'none', color: 'primary.main' }}
            >
              Відкрити зовнішнє посилання
            </Link>
            <Divider sx={{ my: 1.5 }} />
          </>
        )}

        {/* Деталі активності (Назва з довідника) - Внизу з зірочкою */}
        {metActivityName && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4, fontStyle: 'italic' }}>
              * Тип активності (MET): {metActivityName}
            </Typography>
          </>
        )}

      </DialogContent>
      
      <DialogActions>
        {onEdit && (
          <Button onClick={() => { onClose(); onEdit(event); }} color="primary">
            Редагувати
          </Button>
        )}
        <Button onClick={onClose}>Закрити</Button>
      </DialogActions>
    </Dialog>
  );
};
